/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import type { HTMLAttributes } from 'react';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import { type ContextValue, Provider, UNSTABLE_ToastContent as ToastContentCore, useContextProps } from 'react-aria-components';
import { CloseStrokeIcon } from '../../icons/close-stroke/close-stroke.icon';
import { $ } from '../../tokens.stylex';
import { IconButtonContext } from '../icon-button/icon-button.component';
import type { IconProps } from '../icon/icon.component';
import { IconContext } from '../icon/icon.component';
import { TextButtonContext } from '../text-button/text-button.component';
import type { TextProps } from '../text/text.component';
import { TextContext } from '../text/text.component';

interface SnackbarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'className'> {
  /** The variant of the snackbar. (Default: primary) */
  variant?: keyof typeof variantVariants;
  style?: stylex.StyleXStyles;
}

const SnackbarContext = createContext<ContextValue<SnackbarProps, HTMLDivElement>>({});

const Snackbar = forwardRef((props: SnackbarProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, SnackbarContext);

  const { style, variant = 'primary', children, ...coreProps } = props;

  return (
    <ToastContentCore ref={ref} {...stylex.props(styles.base, variantVariants[variant], style)} {...coreProps}>
      <Provider values={[
        [TextContext, {
          size: 13,
          color: TEXT_COLORS[variant],
          style: textStyles.base,
        }],
        [IconContext, {
          size: 20,
          color: ICON_COLORS[variant],
          children: ICON_CONTENTS[variant],
          style: iconStyles.base,
        }],
        [TextButtonContext, {
          size: 32,
          safeArea: 'rounded',
          style: buttonVariantVariants[variant],
          UNSAFE_TextContext: (renderProps) => ({
            style: [
              buttonContentStyles.base,
              buttonContentVariantVariants[variant],
              (renderProps.isHovered && variant === 'secondary') && buttonContentVariantVariants.secondaryHover,
              (renderProps.isHovered && variant === 'contrast') && buttonContentVariantVariants.contrastHover,
            ],
          })
        }],
        [IconButtonContext, {
          slots: {
            [SNACKBAR_SLOTS.CLOSE]: {
              size: 28,
              isRounded: true,
              variant: 'ghost',
              style: buttonVariantVariants[variant],
              children: <CloseStrokeIcon />,
              UNSAFE_IconContext: (renderProps) => ({
                style: [
                  iconButtonIconVariantVariants[variant],
                  (renderProps.isHovered && variant === 'secondary') && iconButtonIconVariantVariants.secondaryHover,
                  (renderProps.isHovered && variant === 'contrast') && iconButtonIconVariantVariants.contrastHover,
                ],
              }),
              "aria-label": 'Close',
            }
          }
        }],
      ]}>
        {children}
      </Provider>
    </ToastContentCore>
  );
});

const ICON_COLORS: Record<keyof typeof variantVariants, IconProps['color']> = {
  primary: 'primary',
  secondary: 'tertiary',
  error: 'primary',
  warning: 'primary',
  success: 'primary',
  info: 'primary',
  contrast: 'inverted',
};

const ICON_CONTENTS: Record<keyof typeof variantVariants, IconProps['children']> = {
  primary: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
      <path d="M10 14.1666C10 13.8333 10 11.8055 10 9.58331M10.0001 7.08331V5.83331M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  secondary: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
      <path d="M10 14.1666C10 13.8333 10 11.8055 10 9.58331M10.0001 7.08331V5.83331M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  error: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
      <path d="M13.75 6.25L10 10M10 10L6.25 13.75M10 10L13.75 13.75M10 10L6.25 6.25M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  warning: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
      <path d="M10 14.1666C10 13.8333 10 11.8055 10 9.58331M10.0001 7.08331V5.83331M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  success: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
      <path d="M5.8999 10L8.86984 12.8732L14.6002 7.29992M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  info: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
      <path d="M10 14.1666C10 13.8333 10 11.8055 10 9.58331M10.0001 7.08331V5.83331M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  contrast: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
      <path d="M10 14.1666C10 13.8333 10 11.8055 10 9.58331M10.0001 7.08331V5.83331M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const TEXT_COLORS: Record<keyof typeof variantVariants, TextProps['color']> = {
  primary: 'primary',
  secondary: 'tertiary',
  error: 'primary',
  warning: 'primary',
  success: 'primary',
  info: 'primary',
  contrast: 'inverted',
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    borderRadius: 10,
    borderStyle: 'solid',
    borderWidth: 1,
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    height: 48,
    minWidth: 358,
    paddingBottom: '8px',
    paddingLeft: '12px',
    paddingRight: '8px',
    paddingTop: '8px',
  },
});

const variantVariants = stylex.create({
  primary: {
    backgroundColor: $.surface00,
    borderColor: $.border08,
    boxShadow: $.shadowCenter050Medium,
  },
  secondary: {
    backgroundColor: $.surface00,
    borderColor: $.border08,
    boxShadow: $.shadowCenter050Medium,
  },
  error: {
    backgroundColor: $.surfaceCriticalMedium,
    borderColor: $.accentChilli10,
    boxShadow: $.shadowCenter050Medium,
  },
  warning: {
    backgroundColor: $.surfaceAttentiveSilent,
    borderColor: $.colorSun10,
    boxShadow: $.shadowCenter050Medium,
  },
  success: {
    backgroundColor: $.surfacePositiveSilent,
    borderColor: $.accentCucumber10,
    boxShadow: $.shadowCenter050Medium,
  },
  info: {
    backgroundColor: $.surfaceHighlightSilent,
    borderColor: $.borderInteractiveSilent,
    boxShadow: $.shadowCenter050Medium,
  },
  contrast: {
    backgroundColor: $.opacity80,
    borderColor: $.border08,
  },
});

const textStyles = stylex.create({
  base: {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    display: '-webkit-box',
    marginRight: 8,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  }
});

const buttonVariantVariants = stylex.create({
  primary: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceTertiary,
    },
  },
  secondary: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceAlternativeHover,
    },
  },
  error: {
    backgroundColor: {
      default: 'transparent',
      // TODO: once the design team has fixed the tokens, replace the color with the token
      ':is([data-hovered], [data-pressed], [data-pending])': $.colorChilli10,
    },
  },
  warning: {
    backgroundColor: {
      default: 'transparent',
      // TODO: once the design team has fixed the tokens, replace the color with the token
      ':is([data-hovered], [data-pressed], [data-pending])': $.colorSun10,
    },
  },
  success: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.accentCucumber10,
    },
  },
  info: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.accentSky10,
    },
  },
  contrast: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed], [data-pending])': $.buttonSurfaceTertiaryHover,
    },
  },
});

const buttonContentStyles = stylex.create({
  base: {
    textWrap: 'nowrap',
  },
});

const buttonContentVariantVariants = stylex.create({
  primary: { color: $.buttonContentTertiary },
  secondary: { color: $.textTertiary },
  secondaryHover: { color: $.textPrimary },
  error: { color: $.textCritical },
  warning: { color: $.colorSun80 },
  success: { color: $.colorCucumber80 },
  info: { color: $.colorSky80 },
  contrast: { color: $.buttonContentTertiary },
  contrastHover: { color: $.buttonContentPrimary },
});

const iconButtonIconVariantVariants = stylex.create({
  primary: { fill: $.buttonContentTertiary, stroke: $.buttonContentTertiary, },
  secondary: { fill: $.textTertiary, stroke: $.textTertiary, },
  secondaryHover: { fill: $.textPrimary, stroke: $.textPrimary, },
  error: { fill: $.textCritical, stroke: $.textCritical, },
  warning: { fill: $.accentSun80, stroke: $.accentSun80, },
  success: { fill: $.accentCucumber80, stroke: $.accentCucumber80, },
  info: { fill: $.accentSky80, stroke: $.accentSky80, },
  contrast: { fill: $.buttonContentTertiary, stroke: $.buttonContentTertiary, },
  contrastHover: { fill: $.buttonContentPrimary, stroke: $.buttonContentPrimary, },
});

const iconStyles = stylex.create({
  base: { marginRight: 10 },
});

const SNACKBAR_SLOTS = {
  CLOSE: 'close',
};

export { SNACKBAR_SLOTS, Snackbar, SnackbarContext, type SnackbarProps };
